<template>
  <NuxtLayout
    name="default"
    :header="header"
    :footer="footer"
    :page-story="pageStory"
  >
    <StoryblokComponent :blok="pageStory.content" :story="pageStory" />
  </NuxtLayout>
</template>

<script setup>
import SimpleHeader from "@/components/SimpleHeader.vue";
import GlobalHeader from "@/components/GlobalHeader.vue";
import SimpleFooter from "@/components/SimpleFooter.vue";
import GlobalFooter from "@/components/GlobalFooter.vue";
import { useSettings } from "../composables/MicroCopy";

const route = useRoute();
const config = useRuntimeConfig();
const datasourcesStore = useDatasourcesStore();

let story;

const barePath = route.path.replace(/\/$/, "") || "/";

// Probe should be handled by server/routes/health.js (silent 200).
// If we land here, the Nitro route missed — log and fail closed (no Storyblok).
if (barePath === "/health" || barePath === "/_health") {
  console.warn(
    `[health] probe missed server route, hit [...slug] (${barePath})`
  );
  throw createError({
    statusCode: 503,
    statusMessage: "Health route not mounted",
  });
}

if (barePath === "/home") {
  throw createError({
    statusCode: 404,
  });
}

const countryPrefix = config.public["storyblokCountryPrefix"];

const path = computed(() => {
  const [facilityType, region, facility, hub, news] = route.params.slug ?? [];
  if (route.path === "/") {
    return `${countryPrefix}/home`;
  } else {
    return `${countryPrefix}${route.path}`;
  }
});

if (!datasourcesStore.countrySettings?.length) {
  await datasourcesStore.fetchCountrySettings();
}

const authorOverrideToPersonTemplates = parseStructuredDataTemplates(
  datasourcesStore.findCountrySetting(
    "structured-data-author-override-to-person"
  )
);
const authorResolveRelations = buildAuthorResolveRelations(
  authorOverrideToPersonTemplates
);

const baseResolveRelations = [
  "facility-overview.additional_links",
  "page-landing-event.facility",
  "faq-list.items",
  "collection-news.folder_name",
  "collection-news.facility_news_folder_name",
];

const resolveRelations = [
  ...baseResolveRelations,
  ...(authorResolveRelations ? authorResolveRelations.split(",") : []),
].join(",");

try {
  const { story: fetchedStory, rels } = await useAsyncStoryblokWithRels(
    path.value,
    {
      version: config.public["storyblokVersion"],
      resolve_relations: resolveRelations,
      resolve_links: "url",
    }
  );
  story = fetchedStory;
  inlineStoryAuthors(story.value.content, rels.value);
} catch (error) {
  let parsedError;
  try {
    parsedError = typeof error === "string" ? JSON.parse(error) : error;
  } catch {
    parsedError = error;
  }
  const status = Number(parsedError?.status) || 500;
  const rawMessage =
    status === 404
      ? `Story with path "${path.value}" not found`
      : parsedError?.message ?? error;
  const statusMessage =
    typeof rawMessage === "string"
      ? rawMessage
      : rawMessage?.message
        ? String(rawMessage.message)
        : String(rawMessage);

  if (process.client) {
    showError({ message: statusMessage, statusCode: status });
    window.location.reload(true);
  }

  throw createError({
    statusCode: status,
    statusMessage,
  });
}

const pageStory = computed(() => story?.value ?? null);

const header = computed(() =>
  story.value.content.component === "page-landing-event"
    ? SimpleHeader
    : GlobalHeader
);

const footer = computed(() =>
  story.value.content.component === "page-landing-event"
    ? SimpleFooter
    : GlobalFooter
);

definePageMeta({ layout: false });

const facilityPageTypes = useSettings('facility-page-content-type')
const isFacilityPage = facilityPageTypes?.split(',').includes(story.value.content.component)
const isDepartmentalPage = story.value.content.component === 'page-landing-region'

if ((isFacilityPage || isDepartmentalPage) && story.value.content.facilityTypeGroupKey) {
  const facilityType = Array.isArray(story.value.content.facilityTypeGroupKey)
    ? story.value.content.facilityTypeGroupKey[0]
    : story.value.content.facilityTypeGroupKey

  const metaTitle = useSettings(`metatitle-${facilityType}`)
  const overwriting_metatitle = useSettings('overwriting-metatitle')

  if (metaTitle && overwriting_metatitle === 'true') {
    if (!isDepartmentalPage || !story.value.content.og_title) {
      story.value.content.og_title = metaTitle.replace(/\[(\w+)\]/g, (_, key) => story.value.content[key] || "")
    }
  }
}

useCustomSeoMeta(story.value.content);
</script>
